import { AttachingMethod, Attachment, AttachmentType } from '../../../../types';
import { assertExists } from '../../../../util/guards';
import { logger } from '../../../util/logger';
import { renderModel } from '../../../util/renderModel';
import { httpClient } from '../../httpClient';
import { getMediaAuthToken } from '../getMediaToken';
import { getMediaType } from './getMediaType';
import { getUploadUrl } from './getUploadUrl';
import { UploadFileResponse } from './types';

const createImageUrl = (path: string) => new URL(path, 'https://static.wixstatic.com').href;
const createVideoUrl = (path: string) => new URL(path, 'https://video.wixstatic.com').href;

const fileMapper = (file: UploadFileResponse, attachingMethod: AttachingMethod): Attachment => {
  if (file.media_type === 'picture') {
    return {
      type: AttachmentType.Image,
      name: file.original_file_name,
      width: file.width,
      height: file.height,
      size: file.file_size,
      url: createImageUrl(file.file_url),
      attachingMethod,
      mimeType: file.mime_type,
    };
  }

  const videoData = file.file_output.video[0];

  assertExists(videoData, 'file_output.video should be presented');

  return {
    type: AttachmentType.Video,
    name: file.original_file_name,
    size: file.file_size,
    width: videoData.width,
    height: videoData.height,
    duration: videoData.duration,
    format: videoData.format,
    url: createVideoUrl(videoData.url),
    iconUrl: createImageUrl(file.icon_url),
    attachingMethod,
    mimeType: file.mime_type,
  };
};

const createBody = (file: File, uploadToken: string): FormData => {
  const formData = new FormData();
  const mediaType = getMediaType(file);

  formData.set('upload_token', uploadToken);
  formData.set('media_type', mediaType);
  formData.set('parent_folder_id', 'visitor-uploads');
  formData.set('file', file, file.name);

  return formData;
};

export const uploadMedia = async (
  files: File[],
  attachingMethod: AttachingMethod,
  signal: AbortSignal,
): Promise<Attachment[]> => {
  const mediaAuthToken = await getMediaAuthToken();

  try {
    return Promise.all(
      files.map(async (file) => {
        const { upload_url, upload_token } = await getUploadUrl(file, mediaAuthToken);
        const body = createBody(file, upload_token);
        const source = httpClient.getInstance().CancelToken.source();
        signal.onabort = () => source.cancel();

        const {
          data: [uploadedFile],
        } = await httpClient
          .getInstance()
          .post<UploadFileResponse[]>(upload_url, body, { cancelToken: source.token });

        return fileMapper(uploadedFile, attachingMethod);
      }),
    );
  } catch (error) {
    logger.error({
      message: 'fail to upload media',
      error,
      callParams: renderModel.get(),
    });
    throw error;
  }
};
